import { useEffect, useRef } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/primitives/Button';
import { RouteGateFallback } from '@/components/routing/RouteGateFallback';
import { AccountProfileError } from '@/components/routing/AccountProfileError';
import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';
import {
  getNativeAccountCompletionPath,
  isAnonymousUser,
  readNativeAccountCompletion,
} from '@/utils/nativeAccountCompletion';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Skip onboarding check for this route */
  skipOnboardingCheck?: boolean;
}

interface LocationState {
  fromOnboarding?: boolean;
}

function buildOnboardingRedirectPath(pathname: string, search: string, hash: string): string {
  const rawPath = `${pathname}${search}${hash}`;
  if (!rawPath.startsWith('/') || rawPath.startsWith('//')) {
    return '/onboarding';
  }
  return `/onboarding?redirect=${encodeURIComponent(rawPath)}`;
}

// V7 onboarding feature launch date - users created before this are grandfathered
const V7_ONBOARDING_CUTOFF = new Date('2025-12-13T00:00:00Z');

type SignOutHandler = () => Promise<{ error: unknown }>;

function SuspendedAccountNotice({ onSignOut, route }: { onSignOut: SignOutHandler; route: string }) {
  const reportedRouteRef = useRef<string | null>(null);

  useEffect(() => {
    if (reportedRouteRef.current === route) return;
    reportedRouteRef.current = route;

    void supabase.functions.invoke('record-security-event', {
      body: {
        eventType: 'suspended_account_route_block',
        route,
      },
    }).catch(() => undefined);
  }, [route]);

  const handleSignOut = () => {
    void onSignOut();
  };

  return (
    <div className="min-h-screen bg-jp-bg-page text-jp-fg-primary">
      <main className="mx-auto flex min-h-screen w-full max-w-3xl flex-col justify-center px-4 py-12 sm:px-6">
        <section className="rounded-v7-lg border border-jp-line-edge bg-jp-bg-card p-6 shadow-[shadow:var(--jp-shadow-card)] sm:p-8">
          <p className="font-v7-body text-sm font-bold uppercase text-jp-accent-primary">
            Account suspended
          </p>
          <h1 className="mt-4 font-v7-headline text-3xl font-bold leading-tight text-jp-fg-primary sm:text-4xl">
            This Journal Party account is currently unavailable.
          </h1>
          <p className="mt-4 font-v7-body text-base leading-7 text-jp-fg-secondary">
            We suspended access after a review of activity connected to this account. If you believe this is a mistake,
            contact us from the email address on the account and include any context you want us to review.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Button asChild>
              <a href="mailto:legal@journalparty.com?subject=Suspended%20Journal%20Party%20account">
                Contact Journal Party
              </a>
            </Button>
            <Button type="button" variant="secondary" onClick={handleSignOut}>
              Sign out
            </Button>
          </div>
          <p className="mt-5 font-v7-body text-sm text-jp-fg-tertiary">
            You can review our <Link className="font-bold text-jp-accent-primary underline-offset-4 hover:underline" to="/terms">Terms of Service</Link>.
          </p>
        </section>
      </main>
    </div>
  );
}

const ProtectedRoute = ({ children, skipOnboardingCheck = false }: ProtectedRouteProps) => {
  const { user, profile, loading, profileLoading, profileLoadFailed, refreshProfile, signOut } = useAuth();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const hasCurrentUserProfile = Boolean(user?.id && profile?.auth_id === user.id);

  // The server cannot know the browser session. Match AuthProvider's initial
  // loading state so protected routes hydrate the same fallback markup instead
  // of rendering an empty redirect boundary on the server and a loader on the
  // client.
  if (typeof window === 'undefined') {
    return <RouteGateFallback />;
  }

  // Gate the first profile load (and an account switch), but keep an already
  // verified user's screen mounted during background profile refreshes. The
  // latter happen after subscription sync and must not destroy local route
  // state such as the current onboarding step.
  if (loading || (Boolean(user) && profileLoading && !hasCurrentUserProfile)) {
    return <RouteGateFallback />;
  }

  if (user && profileLoadFailed && !hasCurrentUserProfile) {
    return <AccountProfileError retry={refreshProfile} />;
  }

  if (!user) {
    const redirectPath = `${location.pathname}${location.search}${location.hash}`;
    if (Capacitor.isNativePlatform()) {
      // A hosted checkout or explicit membership recovery can hand a paid
      // customer back to an app with no local session. Preserve that return
      // and take them straight to sign-in instead of creating a new guest.
      const searchParams = new URLSearchParams(location.search);
      if (
        location.pathname === '/welcome-premium' &&
        (searchParams.get('checkout') === 'success' ||
          searchParams.get('recover') === 'existing')
      ) {
        return <Navigate to={`/auth?mode=signin&redirect=${encodeURIComponent(redirectPath)}`} replace />;
      }
      return <Navigate to="/" replace />;
    }
    return <Navigate to={`/auth?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  if (profile?.account_status === 'suspended' || profile?.is_suspended === true) {
    const route = `${location.pathname}${location.search}${location.hash}`;
    return <SuspendedAccountNotice onSignOut={signOut} route={route} />;
  }

  // Check if user needs onboarding (Epic 7)
  // Skip check if:
  // - Already on onboarding page
  // - Route explicitly skips onboarding check
  // - Profile not loaded yet
  // - Coming from onboarding completion (avoids race condition with profile state)
  // - User was created before v7 onboarding feature (grandfathered)
  const isOnboardingPage = location.pathname === '/onboarding';
  const fromOnboarding = locationState?.fromOnboarding === true;

  // Grandfather existing users: if created before v7 onboarding, skip the check
  // This is a safety net in case the database backfill migration hasn't run yet
  const isGrandfatheredUser = profile?.created_at
    ? new Date(profile.created_at) < V7_ONBOARDING_CUTOFF
    : false;

  // record_direct_journey_entry sets onboarding_skipped_at for people who arrive
  // through a Journey link; they go straight to it, not into onboarding.
  const journeyPath = location.pathname.startsWith('/journey/');
  const needsOnboarding = profile && !profile.onboarding_completed_at && !profile.onboarding_skipped_at
    && !(journeyPath && profile.onboarding_skip_unknown) && !isGrandfatheredUser;

  const accountCompletion = readNativeAccountCompletion(user.id);
  if (
    Capacitor.isNativePlatform() &&
    isAnonymousUser(user) &&
    accountCompletion?.status === 'required' &&
    location.pathname !== '/onboarding' &&
    location.pathname !== '/complete-account'
  ) {
    return (
      <Navigate
        to={getNativeAccountCompletionPath(
          accountCompletion.reason,
          accountCompletion.returnPath,
        )}
        replace
      />
    );
  }

  if (!skipOnboardingCheck && !isOnboardingPage && !fromOnboarding && needsOnboarding) {
    return <Navigate to={buildOnboardingRedirectPath(location.pathname, location.search, location.hash)} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
